import React, {
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { URLParamsContext, URLParams } from "../contexts/URLParamsContext";
import { logger } from "../utils/logger";
import { useTenant } from "../contexts/TenantContext";
import { userService } from "../services/userService";
import { webActivityService } from "../services/webActivityService";

// Storage key for local storage
const URL_PARAMS_STORAGE_KEY = "speculo_url_params";

// Define parameter sets for each campaign type
const EMAIL_CAMPAIGN_PARAMS = [
  "campaign_id",
  "contact_id",
  "template",
  "org_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
];

const AD_CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_adgroup",
  "utm_term",
  "utm_content",
  "targetid",
  "adposition",
  "device",
  "network",
  "loc_physical_ms",
  "loc_interest_ms",
  "gclid",
  "adgroupname",
  "campaignname",
];

const META_CAMPAIGN_PARAMS = [
  // Meta-specific parameters
  "fbclid",
  "fb_campaign_id",
  "fb_ad_id",
  // Common UTM parameters
  "utm_source",
  "utm_medium",
  "utm_campaign",
];

// All parameters to track
const TRACKED_PARAMS = [
  ...new Set([
    ...EMAIL_CAMPAIGN_PARAMS,
    ...AD_CAMPAIGN_PARAMS,
    ...META_CAMPAIGN_PARAMS,
    "campaign_type", // Add campaign_type to tracked params
    "agent", // Add agent parameter to track referring agents
  ]),
];

// Function to detect campaign type from parameters
const detectCampaignType = (
  params: Record<string, string | undefined>
): string | undefined => {
  // Use utm_source as the primary indicator of campaign type
  if (params.utm_source) {
    const source = params.utm_source.toLowerCase();

    if (source === "google") {
      return "ad";
    }

    if (source === "email") {
      return "email";
    }

    if (source === "facebook") {
      return "meta";
    }

    // For other utm_source values
    return source; // Use the utm_source value as the campaign type
  }

  // Fallbacks for when utm_source is not present
  if (params.campaign_id && params.contact_id) {
    return "email";
  }

  if (params.gclid) {
    return "ad";
  }

  if (params.fbclid || params.fb_campaign_id) {
    return "meta";
  }

  return undefined;
};

// Storage structure with timestamp
interface StoredCampaignData {
  params: URLParams;
  timestamp: number;
  type?: string;
}

interface URLParamsProviderProps {
  children: ReactNode;
}

export const URLParamsProvider: React.FC<URLParamsProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useTenant();
  const [params, setParams] = useState<URLParams>({});
  const [guestDataFetched, setGuestDataFetched] = useState(false);

  // Parse URL search params and extract tracked parameters
  const parseURLParams = useCallback((search: string): URLParams => {
    const searchParams = new URLSearchParams(search);
    const extractedParams: URLParams = {};

    // Extract all tracked parameters
    TRACKED_PARAMS.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        extractedParams[param] = value;
      }
    });

    return extractedParams;
  }, []);

  // Load params from local storage
  const loadParamsFromStorage = useCallback((): URLParams => {
    try {
      const storedData = localStorage.getItem(URL_PARAMS_STORAGE_KEY);
      if (!storedData) return {};

      // Handle both old format (just params) and new format (with timestamp and type)
      try {
        const data = JSON.parse(storedData) as StoredCampaignData;
        return data.params || {};
      } catch {
        // If parsing as StoredCampaignData fails, try parsing as the old format
        return JSON.parse(storedData) as URLParams;
      }
    } catch (error) {
      logger.error("Error loading URL params from storage", error);
      return {};
    }
  }, []);

  // Save params to local storage with timestamp and type
  const saveParamsToStorage = useCallback(
    (newParams: URLParams, type?: string) => {
      try {
        const data: StoredCampaignData = {
          params: newParams,
          timestamp: Date.now(),
          type: type || newParams.campaign_type,
        };
        localStorage.setItem(URL_PARAMS_STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        logger.error("Error saving URL params to storage", error);
      }
    },
    []
  );

  // Update params with new values
  const updateParams = useCallback(
    (newParams: URLParams) => {
      setParams((prevParams) => {
        const updatedParams = { ...prevParams, ...newParams };

        // Detect campaign type if not provided
        if (!updatedParams.campaign_type) {
          const detectedType = detectCampaignType(updatedParams);
          if (detectedType) {
            updatedParams.campaign_type = detectedType;
          }
        }

        saveParamsToStorage(updatedParams, updatedParams.campaign_type);
        return updatedParams;
      });
    },
    [saveParamsToStorage]
  );

  // Append params to a URL
  const appendParamsToUrl = useCallback(
    (url: string): string => {
      try {
        const urlObj = new URL(url);
        const searchParams = new URLSearchParams(urlObj.search);

        // Add all tracked params that exist in our state
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            searchParams.set(key, value);
          }
        });

        // Update the URL's search params
        urlObj.search = searchParams.toString();
        return urlObj.toString();
      } catch (error) {
        // If URL parsing fails (e.g., for relative URLs), handle manually
        const hasQueryParams = url.includes("?");
        const baseUrl = hasQueryParams ? url.split("?")[0] : url;
        const existingParams = hasQueryParams
          ? new URLSearchParams(url.split("?")[1])
          : new URLSearchParams();

        // Add all tracked params that exist in our state
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            existingParams.set(key, value);
          }
        });

        const queryString = existingParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      }
    },
    [params]
  );

  // Helper methods for campaign types
  const getCampaignType = useCallback(() => {
    return params.campaign_type;
  }, [params]);

  const isEmailCampaign = useCallback(() => {
    return params.campaign_type === "email";
  }, [params]);

  const isAdCampaign = useCallback(() => {
    return params.campaign_type === "ad";
  }, [params]);

  const isMetaCampaign = useCallback(() => {
    return params.campaign_type === "meta";
  }, [params]);

  // Fetch guest data when contact_id is available
  useEffect(() => {
    const fetchGuestData = async () => {
      // Only fetch if we have contact_id and org_id/config and haven't fetched yet
      if (!params.contact_id || !config?.id || guestDataFetched) {
        // Clear guest data if no contact_id
        if (!params.contact_id && guestDataFetched) {
          webActivityService.setGuestData(null);
          setGuestDataFetched(false);
        }
        return;
      }

      try {
        logger.debug("Fetching guest data for web activity tracking", {
          contactId: params.contact_id,
          orgId: config.id,
        });

        const contact = await userService.findContactById(
          params.contact_id,
          config.id
        );

        if (contact) {
          webActivityService.setGuestData({
            contactId: params.contact_id,
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phoneNumber:
              contact.phoneNumber !== "10000000000"
                ? contact.phoneNumber
                : undefined,
          });
          setGuestDataFetched(true);
          logger.debug("Guest data set for web activity tracking", {
            contactId: params.contact_id,
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
          });
        } else {
          logger.debug("Contact not found for guest tracking", {
            contactId: params.contact_id,
          });
          webActivityService.setGuestData(null);
        }
      } catch (err) {
        logger.error("Error fetching guest data for web activity tracking", {
          contactId: params.contact_id,
          error: err instanceof Error ? err.message : "Unknown error",
        });
        webActivityService.setGuestData(null);
      }
    };

    fetchGuestData();
  }, [params.contact_id, config?.id, guestDataFetched]);

  // Track if we're currently updating the URL to prevent loops
  const isUpdatingUrl = useRef(false);

  // Update the URL with stored parameters
  const updateUrlWithParams = useCallback(() => {
    // Don't update if we're at the root path (let the redirect happen first)
    // or if we're already updating or have no params
    if (
      isUpdatingUrl.current ||
      Object.keys(params).length === 0 ||
      location.pathname === "/"
    )
      return;

    // Create new search params
    const searchParams = new URLSearchParams(location.search);

    // Add all tracked params that exist in our state
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      }
    });

    // Update URL without reloading the page
    isUpdatingUrl.current = true;
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true } // Replace current history entry to avoid back button issues
    );

    // Reset flag after a short delay to allow for the navigation to complete
    setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 100);
  }, [params, location.pathname, location.search, navigate]);

  // Initialize params from URL and local storage
  useEffect(() => {
    // Skip if we're currently updating the URL
    if (isUpdatingUrl.current) return;

    const urlParams = parseURLParams(location.search);
    const storedParams = loadParamsFromStorage();

    // Detect campaign types
    const urlCampaignType = detectCampaignType(urlParams);
    const storedCampaignType = storedParams.campaign_type;

    // If URL has campaign parameters, add the type
    if (urlCampaignType) {
      urlParams.campaign_type = urlCampaignType;
    }

    // Sticky attribution: once seen, these follow the visitor across navigation
    // AND new tabs like campaign params, even without a `campaign_type` — the
    // referral `agent`, and the localhost dev `org_id` (so the tenant keeps
    // resolving and org_id stays in the URL; URLParamsProvider is the single URL
    // manager for it). URL wins, else stored, else current.
    const STICKY_PARAMS = ["agent", "org_id"] as const;
    const sticky: URLParams = {};
    STICKY_PARAMS.forEach((key) => {
      const value = urlParams[key] || storedParams[key] || params[key];
      if (value) sticky[key] = value;
    });
    const hasSticky = Object.keys(sticky).length > 0;

    let mergedParams: URLParams | null = null;

    if (Object.keys(urlParams).length > 0 && urlCampaignType) {
      // New campaign in the URL — replace campaign attribution.
      mergedParams = { ...urlParams };
    } else if (Object.keys(storedParams).length > 0) {
      // No campaign in the URL — fall back to stored attribution.
      mergedParams = { ...storedParams };
    } else if (hasSticky) {
      // Only sticky params (no campaign, nothing stored yet).
      mergedParams = {};
    }

    if (mergedParams) {
      // Carry the sticky params onto whatever we resolved.
      Object.assign(mergedParams, sticky);

      if (JSON.stringify(mergedParams) !== JSON.stringify(params)) {
        setParams(mergedParams);
        // Persist when there's a campaign type OR a sticky param, so a bare
        // `?agent=` / `?org_id=` sticks and is restored on new tabs.
        if (mergedParams.campaign_type || hasSticky) {
          saveParamsToStorage(mergedParams, mergedParams.campaign_type);
        }
      }
    } else if (Object.keys(params).length > 0) {
      // Nothing in URL, storage, or sticky — clear stale state.
      setParams({});
    }
  }, [
    location.search,
    parseURLParams,
    loadParamsFromStorage,
    saveParamsToStorage,
    params,
  ]);

  // Update URL when params change
  useEffect(() => {
    // Only update URL if we have parameters and we're not already updating
    if (Object.keys(params).length > 0 && !isUpdatingUrl.current) {
      updateUrlWithParams();
    }
  }, [params, updateUrlWithParams]);

  // Create context value with helper methods
  const contextValue = {
    params,
    updateParams,
    appendParamsToUrl,
    getCampaignType,
    isEmailCampaign,
    isAdCampaign,
    isMetaCampaign,
  };

  return (
    <URLParamsContext.Provider value={contextValue}>
      {children}
    </URLParamsContext.Provider>
  );
};

export default URLParamsProvider;
